import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { DateFilter, buildParams } from "@/components/DateFilter";
import { exportCSV, printTable, downloadXlsx } from "@/lib/export";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { FileDown, Printer, FileSpreadsheet, BarChart3 } from "lucide-react";
import { AllReports } from "@/components/AllReports";

const PIE = ["#1A2A5E", "#F4571E", "#10B981", "#F59E0B", "#8B5CF6", "#06B6D4"];

const REPORTS = [
  { key: "sales", label: "Sales" }, { key: "all", label: "All Reports" }, { key: "purchase", label: "Purchase" }, { key: "expense", label: "Expense" },
  { key: "profit-loss", label: "Profit / Loss" }, { key: "tax", label: "GST / Tax" }, { key: "stock", label: "Stock" },
  { key: "low-stock", label: "Low Stock" }, { key: "product", label: "Product" }, { key: "category", label: "Category" },
  { key: "customer", label: "Customer" }, { key: "vendor", label: "Vendor" }, { key: "staff-sales", label: "Staff Sales" },
  { key: "cash", label: "Cash" }, { key: "credit", label: "Credit / Udhaar" }, { key: "payment", label: "Payment" },
];

const COLUMNS = {
  sales: [{ key: "bill_no", label: "Bill No" }, { key: "date", label: "Date" }, { key: "customer", label: "Customer" }, { key: "subtotal", label: "Subtotal" }, { key: "tax", label: "Tax" }, { key: "total", label: "Total" }, { key: "method", label: "Payment" }],
  purchase: [{ key: "invoice_no", label: "Invoice" }, { key: "date", label: "Date" }, { key: "vendor", label: "Vendor" }, { key: "total", label: "Total" }, { key: "paid", label: "Paid" }, { key: "outstanding", label: "Outstanding" }, { key: "status", label: "Status" }],
  expense: [{ key: "date", label: "Date" }, { key: "category", label: "Category" }, { key: "amount", label: "Amount" }, { key: "method", label: "Method" }, { key: "note", label: "Note" }],
  "profit-loss": [{ key: "item", label: "Item" }, { key: "amount", label: "Amount" }],
  tax: [{ key: "rate", label: "GST %" }, { key: "taxable", label: "Taxable" }, { key: "cgst", label: "CGST" }, { key: "sgst", label: "SGST" }, { key: "total_tax", label: "Total Tax" }],
  stock: [{ key: "name", label: "Product" }, { key: "category", label: "Category" }, { key: "stock", label: "Stock" }, { key: "min_stock", label: "Min" }, { key: "purchase_price", label: "Cost" }, { key: "stock_value", label: "Stock Value" }],
  "low-stock": [{ key: "name", label: "Product" }, { key: "category", label: "Category" }, { key: "stock", label: "Stock" }, { key: "min_stock", label: "Min" }],
  product: [{ key: "name", label: "Product" }, { key: "category", label: "Category" }, { key: "stock", label: "Stock" }, { key: "selling_price", label: "Price" }, { key: "stock_value", label: "Value" }],
  category: [{ key: "category", label: "Category" }, { key: "qty", label: "Qty Sold" }, { key: "revenue", label: "Revenue" }],
  customer: [{ key: "name", label: "Customer" }, { key: "phone", label: "Phone" }, { key: "total_purchases", label: "Total Purchases" }, { key: "balance", label: "Outstanding" }],
  vendor: [{ key: "name", label: "Vendor" }, { key: "phone", label: "Phone" }, { key: "balance", label: "Payable" }],
  "staff-sales": [{ key: "staff", label: "Staff" }, { key: "bills", label: "Bills" }, { key: "total", label: "Total Sales" }],
  cash: [{ key: "item", label: "Item" }, { key: "amount", label: "Amount" }],
  credit: [{ key: "bill_no", label: "Bill No" }, { key: "date", label: "Date" }, { key: "customer", label: "Customer" }, { key: "total", label: "Total" }, { key: "paid", label: "Paid" }, { key: "outstanding", label: "Outstanding" }],
  payment: [{ key: "bill_no", label: "Bill No" }, { key: "date", label: "Date" }, { key: "customer", label: "Customer" }, { key: "total", label: "Total" }, { key: "method", label: "Method" }],
};

const MONEY_KEYS = new Set(["subtotal", "tax", "total", "amount", "taxable", "cgst", "sgst", "total_tax", "purchase_price", "selling_price", "stock_value", "revenue", "balance", "payable", "paid", "outstanding", "total_purchases", "cost"]);

export default function Reports() {
  const [active, setActive] = useState("sales");
  const [filter, setFilter] = useState({ period: "this_month" });

  const { data, isLoading } = useQuery({
    queryKey: ["report", active, filter],
    enabled: active !== "all",
    queryFn: async () => (await api.get(`/reports/${active}`, { params: buildParams(filter) })).data,
  });

  const cols = COLUMNS[active] || [];
  const rows = (data?.rows || []).map((r) => {
    const out = { ...r };
    cols.forEach((c) => {
      if (MONEY_KEYS.has(c.key) && typeof out[c.key] === "number") out[c.key] = money(out[c.key]);
      if (c.key === "date" && out[c.key]) out[c.key] = new Date(out[c.key]).toLocaleDateString("en-IN");
    });
    return out;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">Reports</h2><p className="text-slate-500 text-sm">Analyze performance across any period</p></div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button key={r.key} onClick={() => setActive(r.key)} data-testid={`report-${r.key}`}
            className={`text-sm px-3.5 py-1.5 rounded-full font-medium transition-colors ${active === r.key ? "bg-[#1A2A5E] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#1A2A5E]"}`}>{r.label}</button>
        ))}
      </div>

      {active === "all" ? <AllReports params={buildParams(filter)} /> : <>
      {/* Summary */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.summary).map(([k, v]) => (
            <Card key={k} className="p-4 border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.replace(/_/g, " ")}</div>
              <div className="text-xl font-bold font-mono mt-1 text-[#1A2A5E]">{typeof v === "number" && !["count", "bills"].includes(k) ? money(v) : v}</div>
            </Card>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {data?.chart && (
          <Card className="p-5 border-slate-200 lg:col-span-1">
            <h3 className="font-display font-semibold text-[#0F172A] mb-3">Distribution</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.chart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {data.chart.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card className={`border-slate-200 overflow-hidden ${data?.chart ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-display font-semibold text-[#0F172A]">{REPORTS.find((r) => r.key === active)?.label} Report</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadXlsx(active, buildParams(filter)).catch(() => {})} data-testid="export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => exportCSV(`billone-${active}`, rows, cols)} data-testid="export-csv"><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={() => printTable(REPORTS.find((r) => r.key === active)?.label, rows, cols)} data-testid="export-print"><Printer className="h-4 w-4 mr-1.5" /> Print / PDF</Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[520px]">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{cols.map((c) => <TableHead key={c.key} className={MONEY_KEYS.has(c.key) ? "text-right" : ""}>{c.label}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {isLoading ? <TableRow><TableCell colSpan={cols.length} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
                  : rows.length ? rows.map((r, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      {cols.map((c) => <TableCell key={c.key} className={MONEY_KEYS.has(c.key) ? "text-right font-mono" : ""}>{r[c.key] ?? "—"}</TableCell>)}
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={cols.length} className="text-center py-12 text-slate-400"><BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-40" />No data for this period</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      </>}
    </div>
  );
}
